import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageTransition, FadeIn } from '../components/layout';
import { Check, Loader2, MapPin, AlertTriangle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLL_ATTEMPTS = 8;

export default function CheckoutSuccessPage() {
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setError('Session de paiement introuvable.');
      setIsLoading(false);
      return;
    }

    let timeoutId;

    const fetchOrder = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_URL}/orders/by-session/${sessionId}`);
        setOrder(response.data);
        setError(null);
        if (response.data?.status === 'pending_payment' && attempts < MAX_POLL_ATTEMPTS) {
          timeoutId = setTimeout(() => setAttempts(prev => prev + 1), 1000);
        }
      } catch (err) {
        console.error('Error fetching order by session:', err);
        setError('Impossible de recuperer la commande.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId, attempts]);

  useEffect(() => {
    if (order?.status === 'paid' && !clearedRef.current) {
      clearCart();
      clearedRef.current = true;
    }
  }, [order, clearCart]);

  const renderStatusContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={28} className="animate-spin text-[var(--gold)]" />
          <p className="text-sm text-[var(--charcoal)]/60">Verification du paiement...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle size={28} className="text-red-600" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-3xl mb-3 text-[var(--charcoal)]">
            Une erreur est survenue
          </h1>
          <p className="text-[var(--charcoal)]/60 mb-6">
            {error}
          </p>
          <Link
            to="/checkout"
            className="inline-block px-8 py-4 border border-[var(--charcoal)] text-[var(--charcoal)] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--charcoal)] hover:text-white transition-colors"
          >
            Retour au checkout
          </Link>
        </div>
      );
    }

    if (order?.status === 'failed' || order?.status === 'expired') {
      return (
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle size={28} className="text-red-600" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-3xl mb-3 text-[var(--charcoal)]">
            Paiement non confirme
          </h1>
          <p className="text-[var(--charcoal)]/60 mb-6">
            Votre paiement n'a pas abouti. Vous pouvez reessayer.
          </p>
          <Link
            to="/checkout"
            className="inline-block px-8 py-4 border border-[var(--charcoal)] text-[var(--charcoal)] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--charcoal)] hover:text-white transition-colors"
          >
            Retour au checkout
          </Link>
        </div>
      );
    }

    if (order?.status === 'pending_payment' && attempts >= MAX_POLL_ATTEMPTS) {
      return (
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-[var(--gold)] mx-auto mb-4" />
          <h1 className="font-['Cormorant_Garamond'] text-3xl mb-3 text-[var(--charcoal)]">
            Confirmation en cours
          </h1>
          <p className="text-[var(--charcoal)]/60 mb-6">
            Nous attendons la confirmation Stripe. Cela peut prendre quelques instants.
          </p>
          <Link
            to="/mon-compte"
            className="inline-block px-8 py-4 border border-[var(--charcoal)] text-[var(--charcoal)] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--charcoal)] hover:text-white transition-colors"
          >
            Voir mon compte
          </Link>
        </div>
      );
    }

    if (order?.status !== 'paid') {
      return (
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={28} className="animate-spin text-[var(--gold)]" />
          <p className="text-sm text-[var(--charcoal)]/60">Verification du paiement...</p>
        </div>
      );
    }

    const shipping = order.shipping_address_details;

    return (
      <FadeIn>
        <div className="max-w-[520px] mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="w-20 h-20 mx-auto mb-8 rounded-full bg-green-100 flex items-center justify-center"
          >
            <Check size={40} className="text-green-600" />
          </motion.div>

          <h1 className="font-['Cormorant_Garamond'] text-4xl mb-4 text-[var(--charcoal)]">
            Commande confirmee !
          </h1>

          <p className="text-[var(--charcoal)]/60 mb-2">
            Merci pour votre commande.
          </p>
          <p className="text-sm text-[var(--charcoal)]/50 mb-8">
            N° de commande : <span className="font-mono text-[var(--gold)]">{order.id?.slice(0, 8).toUpperCase()}</span>
          </p>

          {(shipping || order.shipping_address) && (
            <div className="bg-white p-6 border border-[var(--champagne)] mb-8 text-left">
              <h3 className="font-medium text-[var(--charcoal)] mb-3 flex items-center gap-2">
                <MapPin size={16} className="text-[var(--gold)]" />
                Adresse de livraison
              </h3>
              {shipping ? (
                <p className="text-sm text-[var(--charcoal)]/70">
                  {shipping.street}<br />
                  {shipping.complement && <>{shipping.complement}<br /></>}
                  {shipping.postal_code} {shipping.city}<br />
                  {shipping.country}
                </p>
              ) : (
                <p className="text-sm text-[var(--charcoal)]/70">
                  {order.shipping_address}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={`/mon-compte/commandes/${order.id}`}
              className="inline-block px-8 py-4 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors"
            >
              Voir ma commande
            </Link>
            <Link
              to="/"
              className="inline-block px-8 py-4 border border-[var(--charcoal)] text-[var(--charcoal)] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--charcoal)] hover:text-white transition-colors"
            >
              Retour a l'accueil
            </Link>
          </div>
        </div>
      </FadeIn>
    );
  };

  return (
    <PageTransition>
      <section className="min-h-screen flex items-center justify-center px-6 py-20 bg-gradient-to-b from-[var(--champagne)]/30 to-white">
        {renderStatusContent()}
      </section>
    </PageTransition>
  );
}
